import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import {
    Siren, RefreshCw, ChevronDown, ArrowRight, AlertTriangle, IndianRupee,
    Activity, CalendarCheck,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { ACTION_CENTER_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";
import dayjs from "dayjs";
import AlertsPanel from "../sales-agent/AlertsPanel";
import { useSalesAgentAccess } from "../sales-agent/shared";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const SEVERITY = {
    critical: {
        chip: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
        rail: "bg-rose-500",
        card: "border-rose-200 dark:border-rose-900",
    },
    warning: {
        chip: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
        rail: "bg-amber-500",
        card: "border-amber-200 dark:border-amber-900",
    },
};

const LiveChecks = () => {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(false);
    // A notification click passes ?focus=<section>, so the relevant block is
    // already open when the manager lands here.
    const [openKey, setOpenKey] = useState(params.get("focus") || null);
    const focusCustomer = params.get("customer");

    const load = useCallback(() => {
        setLoading(true);
        fetch(ACTION_CENTER_URL)
            .then(res => {
                if (res?.status === "success") setData(res);
                else ToastMsg("error", res?.message || "Failed to load the action center");
            })
            .catch(() => ToastMsg("error", "Failed to load the action center"))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    // Open the first section that actually has something in it, unless a
    // notification already told us where to look.
    useEffect(() => {
        if (!data || openKey) return;
        const first = (data.sections || []).find(s => s.count > 0);
        if (first) setOpenKey(first.key);
    }, [data, openKey]);

    const sections = useMemo(() => data?.sections || [], [data]);
    const openLead = (id) => navigate("/customers/customerdetails", {
        state: { formEdit: true, row: { id } },
    });

    const rowLabel = (section, item) => {
        if (section.key === "backlog") {
            return { name: item.user_name || "Unassigned",
                     meta: `${item.leads} leads past their follow-up date`, metric: item.leads };
        }
        if (section.key === "urgent") {
            return {
                name: item.customer_name, id: item.customer_id, metric: item.priority,
                meta: [item.action_type?.replace(/_/g, " "), item.lead_stage,
                       item.user_name || "Unassigned"].filter(Boolean).join(" · "),
                detail: item.reasoning,
            };
        }
        if (section.key === "unowned") {
            return { name: item.customer_name, id: item.id, metric: item.lead_score,
                     meta: [item.lead_stage, item.contact_number,
                            item.user_name ? `was ${item.user_name}` : "never assigned"]
                            .filter(Boolean).join(" · ") };
        }
        return {
            name: item.customer_name, id: item.customer_id, metric: item.risk_score,
            meta: [item.demo_datetime
                    ? dayjs(item.demo_datetime).format("ddd DD MMM, hh:mm A")
                    : "no date recorded",
                   item.user_name || "Unassigned"].filter(Boolean).join(" · "),
            detail: (item.risk_reasons || []).slice(0, 2).join("; "),
        };
    };

    return (
        <ContainerCard>
            <div className="flex-shrink-0">
                <div className="flex items-center justify-between mb-4 px-1 gap-3 flex-wrap">
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <Siren className="h-4 w-4 text-rose-500" />
                            Action Center
                        </h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {data?.scope === "own"
                                ? "Everything on your plate that needs a decision today"
                                : "Everything the team needs a decision on today, worst first"}
                        </p>
                    </div>
                    <Button onClick={load} size="sm" variant="outline"
                        className="flex items-center gap-1 text-xs" disabled={loading}>
                        <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} /> Refresh
                    </Button>
                </div>

                {data && (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
                            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2">
                                <p className="text-[10px] uppercase tracking-wide text-gray-400">Needs action</p>
                                <p className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">
                                    {data.headline.needs_action}
                                </p>
                            </div>
                            <div className="rounded-lg border border-rose-200 dark:border-rose-900 bg-rose-50/60 dark:bg-rose-900/15 px-3 py-2">
                                <p className="text-[10px] uppercase tracking-wide text-rose-500 dark:text-rose-300">Critical</p>
                                <p className="text-lg font-semibold text-rose-700 dark:text-rose-300 tabular-nums">
                                    {data.headline.critical}
                                </p>
                            </div>
                            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2">
                                <p className="text-[10px] uppercase tracking-wide text-gray-400 flex items-center gap-1">
                                    <IndianRupee className="h-2.5 w-2.5" /> Value exposed
                                </p>
                                <p className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">
                                    {inr(data.headline.value_exposed)}
                                </p>
                            </div>
                            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2">
                                <p className="text-[10px] uppercase tracking-wide text-gray-400">Overdue follow-ups</p>
                                <p className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">
                                    {data.headline.overdue_followups_total}
                                </p>
                            </div>
                        </div>

                        {/* Context, so the alert counts above mean something */}
                        <div className="mb-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 flex items-center gap-5 flex-wrap">
                            <span className="text-[10px] uppercase tracking-wide text-gray-400">Last 7 days</span>
                            <span className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
                                <Activity className="h-3 w-3 text-gray-400" />
                                {data.this_week.activities} activities
                            </span>
                            <span className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
                                <CalendarCheck className="h-3 w-3 text-gray-400" />
                                {data.this_week.demos_done}/{data.this_week.demos_fixed} demos happened
                                {data.this_week.show_rate != null && (
                                    <span className={cn("ml-1 font-medium",
                                        data.this_week.show_rate >= 50
                                            ? "text-emerald-600 dark:text-emerald-400"
                                            : "text-rose-600 dark:text-rose-400")}>
                                        {data.this_week.show_rate}%
                                    </span>
                                )}
                            </span>
                            <span className="text-xs text-gray-600 dark:text-gray-300">
                                {data.this_week.won} won
                            </span>
                            {data.generated_at && (
                                <span className="ml-auto text-[10px] text-gray-400">
                                    as of {dayjs(data.generated_at).format("DD MMM, hh:mm A")}
                                </span>
                            )}
                        </div>
                    </>
                )}
            </div>

            {loading && !data ? (
                <div className="flex items-center justify-center flex-1">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading action center…</p>
                    </div>
                </div>
            ) : data && data.headline.needs_action === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-sm px-6">
                        <CalendarCheck className="h-8 w-8 text-emerald-400 mx-auto mb-3" />
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Nothing needs a decision</p>
                        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                            No missed demos, no unowned leads, no urgent actions outstanding.
                        </p>
                    </div>
                </div>
            ) : (
                <div className="flex-1 min-h-0 overflow-auto space-y-2 pr-0.5">
                    {sections.map(section => {
                        const tone = SEVERITY[section.severity] || SEVERITY.warning;
                        const isOpen = openKey === section.key;
                        const empty = section.count === 0;
                        return (
                            <div key={section.key}
                                className={cn("rounded-xl border bg-white dark:bg-gray-800 overflow-hidden",
                                    empty ? "border-gray-200 dark:border-gray-700 opacity-60" : tone.card)}>
                                <button
                                    onClick={() => setOpenKey(isOpen ? null : section.key)}
                                    disabled={empty}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-left disabled:cursor-default hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:hover:bg-transparent">
                                    <span className={cn("text-sm font-semibold tabular-nums px-2 py-0.5 rounded-md",
                                        empty ? "bg-gray-100 dark:bg-gray-700 text-gray-400" : tone.chip)}>
                                        {section.count}
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-medium text-gray-900 dark:text-white">
                                            {section.title}
                                        </span>
                                        <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                                            {empty ? "Nothing here right now" : section.blurb}
                                        </span>
                                    </span>
                                    {!empty && (
                                        <ChevronDown className={cn("h-4 w-4 text-gray-400 transition-transform flex-shrink-0",
                                            isOpen && "rotate-180")} />
                                    )}
                                </button>

                                {isOpen && !empty && (
                                    <div className="border-t border-gray-100 dark:border-gray-700">
                                        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {section.items.map((item, i) => {
                                                const r = rowLabel(section, item);
                                                const highlighted = focusCustomer &&
                                                    String(r.id) === String(focusCustomer);
                                                return (
                                                    <li key={i}
                                                        onClick={() => r.id && openLead(r.id)}
                                                        className={cn("flex items-start gap-3 px-4 py-2.5",
                                                            r.id && "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40",
                                                            highlighted && "bg-indigo-50 dark:bg-indigo-900/20")}>
                                                        <span className={cn("w-1 self-stretch rounded-full flex-shrink-0", tone.rail)} />
                                                        <span className="min-w-0 flex-1">
                                                            <span className="block text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                                                                {r.name || "—"}
                                                                {highlighted && (
                                                                    <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                                                                        from your alert
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                                                                {r.meta}
                                                            </span>
                                                            {r.detail && (
                                                                <span className="block mt-0.5 text-[11px] text-gray-600 dark:text-gray-300 flex gap-1">
                                                                    <AlertTriangle className="h-3 w-3 text-amber-500 flex-shrink-0 mt-0.5" />
                                                                    <span className="line-clamp-2">{r.detail}</span>
                                                                </span>
                                                            )}
                                                        </span>
                                                        {r.metric != null && (
                                                            <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 tabular-nums flex-shrink-0">
                                                                {r.metric}
                                                            </span>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between gap-2">
                                            {section.count > section.items.length ? (
                                                <span className="text-[10px] text-gray-400">
                                                    showing {section.items.length} of {section.count}
                                                </span>
                                            ) : <span />}
                                            <button onClick={() => navigate(section.link)}
                                                className="text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1">
                                                Work this list <ArrowRight className="h-3 w-3" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </ContainerCard>
    );
};

// With the Sales Manager Agent (sales_agent/), the Action Center opens on the
// tracked alerts: saved, prioritised, with a lifecycle. The live checks above
// stay one tab away, and remain the whole page for organisations without it.
const ActionCenter = () => {
    const { enabled } = useSalesAgentAccess();
    const [params, setParams] = useSearchParams();
    const tab = params.get("tab") === "live" ? "live" : "alerts";
    if (!enabled) return <LiveChecks />;
    const choose = (next) => {
        const p = new URLSearchParams(params);
        if (next === "live") p.set("tab", "live"); else p.delete("tab");
        setParams(p, { replace: true });
    };
    return (
        <div className="space-y-3">
            <div role="tablist" className="flex gap-1 rounded-lg border border-gray-200 bg-white p-1 text-xs dark:border-gray-700 dark:bg-gray-800 w-fit">
                {[["alerts", "Tracked alerts"], ["live", "Live checks"]].map(([key, label]) => (
                    <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => choose(key)}
                        className={cn("rounded-md px-3 py-1.5 font-medium",
                            tab === key ? "bg-primary text-white" : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700")}>
                        {label}
                    </button>
                ))}
            </div>
            {tab === "live" ? <LiveChecks /> : <AlertsPanel />}
        </div>
    );
};

export default ActionCenter;
